import { memo } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconSliders3Outline18 as AiAssistantSettingsIcon,
	IconBoltOutline18 as QuickWinsIcon,
	IconChartBarTrendUpOutline18 as TopRequestsIcon,
	IconChatBubbleClockOutline18 as WaitingOnUsIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { AiChatInput, Button, IconButton, Tooltip } from "@productlane/ui"

import { useCopilotState, useIsCopilotOpen } from "@/lib/states/copilot-state"
import {
	TOP_REQUESTS_PROMPT,
	WAITING_ON_US_PROMPT,
} from "../../threads/components/right-bar/ai-chat/copilot-quick-actions"
import { CopilotToggleButton } from "../../threads/components/right-bar/ai-chat/copilot-toggle-button"
import { InboxAssistantBackdrop } from "./inbox-assistant-backdrop"

const INBOX_ASSISTANT_SUGGESTIONS = [
	{
		label: "Quick wins",
		prompt: "Which threads can I close in a few minutes?",
		Icon: QuickWinsIcon,
	},
	{
		label: "Waiting on us",
		prompt: WAITING_ON_US_PROMPT,
		Icon: WaitingOnUsIcon,
	},
	{
		label: "Top requests",
		prompt: TOP_REQUESTS_PROMPT,
		Icon: TopRequestsIcon,
	},
] as const

export const InboxCopilotEmptyState = memo(function InboxCopilotEmptyState() {
	const navigate = useNavigate()
	const openWith = useCopilotState((s) => s.openWith)
	const openWithVoice = useCopilotState((s) => s.openWithVoice)
	const isCopilotOpen = useIsCopilotOpen({ kind: "workspace" })

	return (
		<div
			className={cn(
				"ease-[cubic-bezier(0.23,1,0.32,1)] relative flex flex-1 flex-col items-center justify-center px-6 transition-[padding] duration-300",
				isCopilotOpen && "pr-[23.5rem]",
			)}
		>
			<div className="absolute right-3 top-3">
				<CopilotToggleButton scope={{ kind: "workspace" }} />
			</div>
			<div className="relative isolate w-full max-w-[552px] select-none">
				<InboxAssistantBackdrop />
				<AiChatInput
					variant="card"
					alwaysPrimarySubmit
					onVoice={() => openWithVoice({ kind: "workspace" })}
					voiceLabel="Ask the assistant by voice"
					placeholder="Ask the assistant..."
					onSubmit={(message) => openWith({ kind: "workspace" }, message)}
					className="rounded-xl [&_button_svg]:translate-x-[0.5px] [&_input]:select-text [&_input]:text-sm [&_label]:rounded-xl [&_label]:ring-black/10 dark:[&_label]:bg-gray-850 dark:[&_label]:shadow-none dark:[&_label]:ring-white/10 dark:[&_label]:hover:bg-gray-850"
				/>
				<div className="mt-3 flex items-center gap-2 pl-0.5">
					<div className="flex flex-wrap gap-2">
						{INBOX_ASSISTANT_SUGGESTIONS.map(({ label, prompt, Icon }) => (
							<Button
								key={label}
								variant="outline-subtle"
								size="sm"
								Icon={Icon}
								iconStrokeWidth={1}
								onClick={() => openWith({ kind: "workspace" }, prompt)}
								className="whitespace-nowrap pl-2 pr-[10px]"
							>
								{label}
							</Button>
						))}
					</div>
					<Tooltip label="AI Assistant settings">
						<IconButton
							Icon={AiAssistantSettingsIcon}
							variant="ghost"
							aria-label="AI Assistant settings"
							iconStrokeWidth={1.25}
							iconClassName="h-4 w-4 text-primary"
							className="ml-auto mr-3 shrink-0 text-primary"
							onClick={() => void navigate({ to: "/settings/ai/assistant" })}
						/>
					</Tooltip>
				</div>
			</div>
		</div>
	)
})
